import { ReactNode } from 'react';
import styled from 'styled-components';
import { buttonColumnGap } from './learn_buttons.tsx';
import { resultColorCss } from './learn_colors.ts';

// The pieces every Learn exercise screen is built from (issues #3473, #3481, #3510, #3518), sized
// from the artboards: the body below the header, the 24 px title and the 15 px line under it, the
// large onboarding-style prompt a screen can open with instead and the one line that may sit under
// it, the slack that pushes the buttons to the foot of the screen, a small row label, and the
// progress row with its label on the left ("Pattern 3 of 8 · 70 bpm", "Warm-up · 70 bpm") and a pip
// per attempt, or per agreeing warm-up tap, on the right. The body is capped at a phone-like width and centred, so on a desktop window an
// exercise reads as a wide phone rather than stretching its buttons across the whole window;
// phones are unaffected. Short screens (iPhone SE) get tighter spacing through the max-height
// rule rather than scrolling.

export const shortScreen = '@media (max-height: 700px)';

export const learnScreenMaxWidth = 480;

export const ScreenBody = styled.div`
  flex: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  max-width: ${learnScreenMaxWidth}px;
  margin: 0 auto;
  padding: 20px;
  overflow-y: auto;

  ${shortScreen} {
    padding: 12px 16px;
  }
`;

export const ScreenTitle = styled.div`
  margin-top: 12px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 24px;
  line-height: 32px;

  ${shortScreen} {
    margin-top: 6px;
    font-size: 22px;
    line-height: 28px;
  }
`;

// The single large prompt an exercise screen opens with (issue #3510). It started at the size of
// onboarding's `Message` (23 px on a 390 x 844 phone), but the user asked for a prompt larger than
// onboarding's message, since an exercise screen carries one sentence and wants it to read from
// arm's length while the singer sings: 28 px, and 24 px where a short screen (iPhone SE) needs the
// room. Left aligned. A prompt may cap itself at `$widthInChars` characters the way onboarding caps
// its width, so it breaks where it reads; the cap is in `ch`, so it grows with the font. Without
// the cap it wraps only where the body's own width forces it, which is what a short prompt wants.
export const ScreenPrompt = styled.div<{ $widthInChars?: number }>`
  margin-top: 12px;
  ${({ $widthInChars }) => ($widthInChars === undefined ? '' : `max-width: ${$widthInChars}ch;`)}
  font-size: 28px;
  line-height: 36px;

  ${shortScreen} {
    margin-top: 6px;
    font-size: 24px;
    line-height: 32px;
  }
`;

export const ScreenSubtitle = styled.div`
  font-size: 15px;
  line-height: 22px;
  color: var(--color-text-darker);
`;

// The one line a screen is allowed under its prompt (issue #3510): the reassurance on the
// permission screen, the reason a microphone failed, the nudge to sing along with the note
export const PromptSubtext = styled(ScreenSubtitle)`
  margin-top: 8px;
`;

// Takes the slack between the content above and the buttons below
export const Slack = styled.div`
  flex: 1 1 0;
  min-height: 12px;
`;

// The buttons at the foot of a screen, with the same air above them as between two buttons of a
// column, which is what the Range Finder's direction screen puts under its meter
export const ScreenFoot = styled.div`
  flex: none;
  padding-top: ${buttonColumnGap}px;
`;

// A small line led by an icon in the accent colour: "Home note set for your range" on Interval
// Training's start screen, "Tap the house any time..." on its listen screen
export const IconLine = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);

  svg {
    flex: none;
    width: 18px;
    height: 18px;
    color: var(--color-tab-selected);
  }
`;

export const RowLabel = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
  margin-bottom: 6px;
`;

// What one pip says: a step behind us, the step we are on, or one still to come. An exercise
// stepping through attempts uses all three (attemptPips); the Rhythm Echo warm-up fills one pip per
// agreeing tap, so it only ever says done or todo.
export type ProgressPipState = 'done' | 'current' | 'todo';

// What a filled pip is filled with: the accent colour, or the result green the warm-up's agreeing
// taps light their blocks in, so its pips and its blocks say the same thing (issue #3523)
export type ProgressPipColor = 'accent' | 'green';

// The pips of an exercise `index` attempts (0-based) into `count`: the ones before it done, the one
// at it current, the rest still to come
export function attemptPips(index: number, count: number): ProgressPipState[] {
  return Array.from({ length: count }, (_pip, pip) => (pip < index ? 'done' : pip === index ? 'current' : 'todo'));
}

// The small row at the head of a screen: a label on the left, a strip of pips on the right. The
// row and pip test ids are the exercise's own, so its stories keep matching them.
export const ProgressPipsRow = ({
  label,
  pips,
  color = 'accent',
  rowTestId,
  pipTestId,
}: {
  label: ReactNode;
  pips: ProgressPipState[];
  color?: ProgressPipColor;
  rowTestId: string;
  pipTestId: string;
}) => (
  <PipsRow data-testid={rowTestId}>
    <span>{label}</span>
    <Pips>
      {Array.from(pips, (state, pip) => (
        <Pip key={pip} $filled={state !== 'todo'} $color={color} data-testid={pipTestId} data-state={state} />
      ))}
    </Pips>
  </PipsRow>
);

const PipsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-darker);
  white-space: nowrap;
`;

const Pips = styled.div`
  display: flex;
  gap: 4px;
`;

const pipFillCss: Record<ProgressPipColor, string> = {
  accent: 'var(--color-tab-selected)',
  green: resultColorCss.green,
};

const Pip = styled.div<{ $filled: boolean; $color: ProgressPipColor }>`
  width: 16px;
  height: 6px;
  border-radius: 3px;
  background-color: ${({ $filled, $color }) => ($filled ? pipFillCss[$color] : 'var(--color-divider)')};
  transition: background-color 120ms linear;
`;
